import React, { useEffect, useState } from "react";
import { Avatar } from "antd";
import { UserOutlined } from "@ant-design/icons";
import { useNavigate } from "react-router-dom";
import Logout from "./Logout";
import axios from "../../utils/axios";
import { Coins, ChevronDown } from "lucide-react";

export default function CustomHeader({ auth }) {
  const navigate = useNavigate();
  const [credits, setCredits] = useState(0);
  const [creditsLoading, setCreditsLoading] = useState(true);

  const userName = auth?.user
    ? auth.user[0].toUpperCase() + auth.user.slice(1)
    : "User";
  
  const organizationId = localStorage.getItem("organizationId");
  const [projectName, setProjectName] = useState(null);

  useEffect(() => {
    const storedProject = localStorage.getItem("selectedProject");
    setProjectName(storedProject || null);
  }, []);

  const hasSelectedProject = !!projectName;

  useEffect(() => {
    const fetchCredits = async () => {
      try {
        setCreditsLoading(true);
        const response = await axios.post("/fetch-credits", {
          username: auth?.user,
          organizationId,
        });
        if (response.data.success) setCredits(response.data.credits);
      } catch (error) {
        console.error("Error fetching credits:", error);
      } finally {
        setCreditsLoading(false);
      }
    };

    if (auth?.user && organizationId) fetchCredits();
  }, [auth, organizationId]);

  const initials = auth?.user
    ? auth.user.slice(0, 2).toUpperCase()
    : "AD";

  return (
    <>
      <style>{`
        .header-root {
          background: #ffffff;
          border-bottom: 1px solid #e8edf5;
          position: sticky;
          top: 0;
          z-index: 50;
          box-shadow: 0 1px 12px rgba(0,0,0,0.06);
        }

        .header-inner {
          max-width: 1440px;
          margin: 0 auto;
          padding: 0 20px;
          height: 64px;
          display: flex;
          align-items: center;
          justify-content: space-between;
          gap: 16px;
        }

        /* LEFT */
        .header-left {
          display: flex;
          align-items: center;
          gap: 14px;
          flex-shrink: 0;
        }
        .header-logo {
          height: 36px;
          width: auto;
          object-fit: contain;
        }
        .header-divider {
          width: 1px;
          height: 28px;
          background: #e2e8f0;
          flex-shrink: 0;
        }
        .header-title {
          font-size: 15px;
          font-weight: 700;
          color: #1A59A7;
          letter-spacing: -0.3px;
          white-space: nowrap;
        }

        /* CENTER */
        .header-center {
          flex: 1;
          display: flex;
          justify-content: center;
          min-width: 0;
        }
        .project-badge {
          display: flex;
          align-items: center;
          gap: 8px;
          padding: 6px 16px;
          background: linear-gradient(135deg, #eff6ff 0%, #f5f3ff 100%);
          border: 1px solid #c7d9fb;
          border-radius: 50px;
          cursor: pointer;
          transition: all 0.2s ease;
          max-width: 320px;
          min-width: 0;
        }
        .project-badge:hover {
          background: linear-gradient(135deg, #dbeafe 0%, #ede9fe 100%);
          border-color: #93c5fd;
          box-shadow: 0 2px 12px rgba(99,102,241,0.12);
          transform: translateY(-1px);
        }
        .project-dot {
          width: 7px;
          height: 7px;
          border-radius: 50%;
          background: #3b82f6;
          flex-shrink: 0;
          box-shadow: 0 0 0 2px rgba(59,130,246,0.2);
        }
        .project-label {
          font-size: 12px;
          color: #6b7280;
          font-weight: 500;
          white-space: nowrap;
          flex-shrink: 0;
        }
        .project-name {
          font-size: 13.5px;
          font-weight: 700;
          color: #1d4ed8;
          white-space: nowrap;
          overflow: hidden;
          text-overflow: ellipsis;
          min-width: 0;
        }
        .project-chevron {
          color: #93c5fd;
          flex-shrink: 0;
        }

        /* RIGHT */
        .header-right {
          display: flex;
          align-items: center;
          gap: 10px;
          flex-shrink: 0;
        }

        /* Credits */
        .credits-pill {
          display: flex;
          align-items: center;
          gap: 6px;
          padding: 6px 14px;
          background: linear-gradient(135deg, #f0fdf4, #dcfce7);
          border: 1px solid #bbf7d0;
          border-radius: 50px;
          transition: all 0.2s;
        }
        .credits-pill:hover {
          box-shadow: 0 2px 10px rgba(34,197,94,0.15);
          transform: translateY(-1px);
        }
        .credits-value {
          font-size: 13px;
          font-weight: 700;
          color: #15803d;
          letter-spacing: -0.2px;
        }
        .credits-label {
          font-size: 11px;
          font-weight: 500;
          color: #4ade80;
        }

        /* User chip */
        .user-chip {
          display: flex;
          align-items: center;
          gap: 8px;
          padding: 4px 12px 4px 4px;
          border-radius: 50px;
          border: 1px solid #e5e7eb;
          background: #f9fafb;
          cursor: default;
          transition: all 0.2s;
        }
        .user-chip:hover {
          background: #f3f4f6;
          border-color: #d1d5db;
        }
        .user-avatar {
          width: 32px !important;
          height: 32px !important;
          font-size: 12px !important;
          font-weight: 700 !important;
          background: linear-gradient(135deg, #3b82f6, #6366f1) !important;
          border: 2px solid #fff !important;
          box-shadow: 0 1px 4px rgba(0,0,0,0.1) !important;
          flex-shrink: 0;
        }
        .user-name {
          font-size: 13px;
          font-weight: 600;
          color: #374151;
          white-space: nowrap;
        }

        /* Action buttons */
        .header-icon-btn {
          width: 36px;
          height: 36px;
          border-radius: 10px;
          display: flex;
          align-items: center;
          justify-content: center;
          border: 1px solid transparent;
          background: transparent;
          cursor: pointer;
          transition: all 0.18s ease;
          color: #6b7280;
        }
        .header-icon-btn:hover {
          background: #f3f4f6;
          border-color: #e5e7eb;
          color: #374151;
        }

        /* Skeleton shimmer for credits */
        @keyframes shimmer {
          0% { background-position: -200px 0 }
          100% { background-position: calc(200px + 100%) 0 }
        }
        .credits-shimmer {
          width: 52px;
          height: 14px;
          border-radius: 4px;
          background: linear-gradient(90deg, #d1fae5 25%, #a7f3d0 50%, #d1fae5 75%);
          background-size: 200px 100%;
          animation: shimmer 1.4s infinite;
        }

        /* Responsive */
        @media (max-width: 768px) {
          .header-title { display: none; }
          .header-divider { display: none; }
          .user-name { display: none; }
          .project-label { display: none; }
          .credits-label { display: none; }
          .project-badge { padding: 6px 12px; }
          .header-inner { padding: 0 12px; gap: 10px; }
        }
        @media (max-width: 480px) {
          .credits-pill { padding: 5px 10px; }
          .user-chip { padding: 4px; border-radius: 50%; }
          .header-logo { height: 28px; }
        }
      `}</style>

      <header className="header-root">
        <div className="header-inner">

          {/* ── LEFT: Logo + Title ─────────────────────────────────── */}
          <div className="header-left">
            <img src="/citriot_logo.webp" alt="Citriot" className="header-logo" />
            <div className="header-divider" />
            <span className="header-title">Admin Dashboard</span>
          </div>

          {/* ── CENTER: Project Badge ──────────────────────────────── */}
          <div className="header-center">
            {hasSelectedProject && (
              <div
                className="project-badge"
                onClick={() => navigate("/select-project")}
                title={`Active project: ${projectName}`}
              >
                <div className="project-dot" />
                <span className="project-label">Project:</span>
                <span className="project-name">{projectName}</span>
                <ChevronDown size={13} className="project-chevron" />
              </div>
            )}
          </div>

          {/* ── RIGHT: Credits + User + Actions ───────────────────── */}
          <div className="header-right">

            {/* Credits */}
            <div className="credits-pill" title="Available credits">
              <Coins size={14} color="#16a34a" strokeWidth={2.5} />
              {creditsLoading ? (
                <div className="credits-shimmer" />
              ) : (
                <>
                  <span className="credits-value">
                    {(credits ?? 0).toLocaleString()}
                  </span>
                  <span className="credits-label">credits</span>
                </>
              )}
            </div>

            {/* User chip */}
            <div className="user-chip">
              <Avatar className="user-avatar" size={32} icon={<UserOutlined />}>
                {initials}
              </Avatar>
              <span className="user-name">{userName}</span>
            </div>

            {/* Logout */}
            <div className="header-icon-btn" title="Logout">
              <Logout />
            </div>

          </div>
        </div>
      </header>
    </>
  );
}